/**
 * Tracks elapsed durations for task steps on the client side.
 *
 * Keys by step label so inserts/reorders in the steps array (e.g. refresh_resource
 * item rows) do not corrupt timers. Failed steps keep backend error text in
 * ``detail``; durations are exposed via ``durationMs`` only.
 */
import type { TaskStep, TaskStepWithDuration } from "@/services/api/data-hub/types";

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function stepKey(step: TaskStep, index: number): string {
  return `${step.kind ?? "step"}:${step.label}:${index}`;
}

export class StepDurationTracker {
  private startTimes = new Map<string, number>();
  private durations = new Map<string, number>();
  private lastStructureKey = "";

  /**
   * Compute durations for each step and return enriched copies.
   */
  update(steps: TaskStep[]): TaskStepWithDuration[] {
    if (steps.length === 0) {
      this.reset();
      return [];
    }

    const structureKey = steps.map((s) => `${s.kind ?? "step"}:${s.label}`).join("|");
    if (structureKey !== this.lastStructureKey) {
      this._pruneStaleKeys(steps);
      this.lastStructureKey = structureKey;
    }

    const now = Date.now();

    return steps.map((step, i) => {
      const key = stepKey(step, i);

      if ((step.status === "running" || step.status === "stalled") && !this.startTimes.has(key)) {
        this.startTimes.set(key, now);
      }

      if (
        (step.status === "done" || step.status === "failed") &&
        this.startTimes.has(key) &&
        !this.durations.has(key)
      ) {
        this.durations.set(key, now - this.startTimes.get(key)!);
      }

      const durationMs = this.durations.get(key);
      const isFailed = step.status === "failed";
      const detail =
        isFailed && step.detail
          ? step.detail
          : durationMs != null && step.status === "done"
            ? formatDuration(durationMs)
            : step.detail;

      return { ...step, detail, durationMs };
    });
  }

  private _pruneStaleKeys(steps: TaskStep[]): void {
    const valid = new Set(steps.map((s, i) => stepKey(s, i)));
    for (const key of [...this.startTimes.keys()]) {
      if (!valid.has(key)) this.startTimes.delete(key);
    }
    for (const key of [...this.durations.keys()]) {
      if (!valid.has(key)) this.durations.delete(key);
    }
  }

  reset(): void {
    this.startTimes.clear();
    this.durations.clear();
    this.lastStructureKey = "";
  }
}
